import { clients } from 'lib/https';
import {
  DisableUserMfaRequest,
  DisableUserMfaResponse,
  GenerateOTPPayload,
  GenerateOTPResponse,
  IMFAConfiguration,
  ManageUserMFA,
  SetUpTotp,
  VerifyOTP,
  VerifyOTPResponse,
} from '../types/mfa.types';
import { ConfigurationSettings } from '/models/configuration-settings';

/**
 * Generates a One-Time Password (OTP) for the user.
 *
 * @param {string} params.userId - The user ID for whom the OTP is to be generated.
 *
 * @returns {Promise<GenerateOTPResponse>} A promise that resolves with the OTP generation response.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const otpResponse = await generateOTP({ userId: '12345' });
 * @param payload
 */
export const generateOTP = async (
  payload: GenerateOTPPayload,
  config: ConfigurationSettings
): Promise<GenerateOTPResponse> => {
  const requestPayload = {
    ...payload,
    projectKey: config.blocksKey,
  };
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.mfa.generateOTP}`;
  return await clients.post<GenerateOTPResponse>(
    url,
    config.blocksKey,
    JSON.stringify(requestPayload)
  );
};

/**
 * Verifies the OTP entered by the user.
 * Sends the OTP and necessary details to the server for validation.
 *
 * @param {VerifyOTP} payload - The OTP details including the verification code, mfaId, and authType.
 * @returns {Promise<VerifyOTPResponse>} The response indicating the success and validity of the OTP.
 * @throws {Error} Throws an error if the verification request fails.
 *
 * @example
 * const payload = { verificationCode: '123456', mfaId: 'mfa-id-123', authType: 1 };
 * const response = await verifyOTP(payload);
 * if (response.isSuccess && response.isValid) {
 *   console.log('OTP verified');
 * } else {
 *   console.log('Invalid OTP');
 * }
 */
export const verifyOTP = async (
  payload: VerifyOTP,
  config: ConfigurationSettings
): Promise<VerifyOTPResponse> => {
  const verifyOTPPayload = {
    ...payload,
    projectKey: config.blocksKey,
  };
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.mfa.verifyOTP}`;
  return await clients.post<VerifyOTPResponse>(
    url,
    config.blocksKey,
    JSON.stringify(verifyOTPPayload)
  );
};

/**
 * Configures Multi-Factor Authentication (MFA) for the user.
 * Sends the MFA settings to the server to enable or disable MFA.
 *
 * @param {ManageUserMFA} payload - The MFA configuration details including the user ID and MFA settings.
 * @returns {Promise<ManageUserMFA>} The updated MFA configuration response.
 * @throws {Error} Throws an error if the configuration request fails.
 *
 * @example
 * const payload = { userId: 'user-id-123', mfaEnabled: true, userMfaType: 1 };
 * const response = await configureUserMfa(payload);
 * console.log(response); // Updated MFA settings
 */
export const configureUserMfa = async (
  payload: ManageUserMFA,
  config: ConfigurationSettings
): Promise<ManageUserMFA> => {
  const configureUserMfaPayload = {
    ...payload,
    projectKey: config.blocksKey,
  };
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.mfa.configureUserMfa}`;
  const res = await clients.post<{ data: ManageUserMFA }>(
    url,
    config.blocksKey,
    JSON.stringify(configureUserMfaPayload)
  );
  return res.data;
};

/**
 * Retrieves the TOTP (Time-based One-Time Password) setup details for the user.
 * Sends a GET request to fetch TOTP configuration using the provided query parameters.
 *
 * @param {Object} context - The context object containing query parameters for the request.
 * @param {SetUpTotp} context.queryKey - The query parameters to set up TOTP.
 * @returns {Promise<any>} The TOTP setup details in the response.
 * @throws {Error} Throws an error if the TOTP setup retrieval fails.
 *
 * @example
 * const queryParams = { userId: 'user-id-123', action: 'enable' };
 * const response = await getSetUpTotp({ queryKey: ['setUpTotp', queryParams] });
 * console.log(response); // TOTP setup details
 */
export const getSetUpTotp = async (
  context: { queryKey: [string, SetUpTotp] },
  config: ConfigurationSettings
): Promise<any> => {
  const [, queryParams] = context.queryKey;
  const stringifiedParams = Object.fromEntries(
    Object.entries(queryParams).map(([key, value]) => [key, String(value)])
  );
  const params = new URLSearchParams(stringifiedParams as Record<string, string>);
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.mfa.setUpTotp}?${params.toString()}`;
  const res = await clients.get<any>(url, config.blocksKey);

  return res;
};

/**
 * Sends a request to resend the OTP to the user based on the provided MFA ID.
 *
 * @param {string} mfaId - The MFA ID associated with the user for OTP resending.
 * @returns {Promise<any>} The response from the OTP resend request.
 * @throws {Error} Throws an error if the OTP resend fails.
 *
 * @example
 * const mfaId = 'user-mfa-id';
 * const response = await resendOtp(mfaId);
 * console.log(response); // Response after resending OTP
 */
export const resendOtp = async (mfaId: string, config: ConfigurationSettings): Promise<any> => {
  const payload = {
    MfaId: mfaId,
  };
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.mfa.resendOTP}`;
  return await clients.post<any>(url, config.blocksKey, JSON.stringify(payload));
};

/**
 * Retrieves the MFA template configuration for the current project.
 * This service makes a GET request to fetch the MFA configuration settings.
 *
 * @returns {Promise<IGetConfigurationResponse>} A promise that resolves with the MFA template configuration.
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const template = await getMfaTemplate();
 * if (template.isSuccess) {
 *   console.log('MFA template retrieved successfully');
 * }
 */
export const getMfaTemplate = async (config: ConfigurationSettings): Promise<IMFAConfiguration> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.mfa.getConfiguration}?${config.blocksKey}`;
  return await clients.get<IMFAConfiguration>(url, config.blocksKey);
};

/**
 * Disables Multi-Factor Authentication (MFA) for a specific user.
 * This service sends a POST request to disable MFA using the provided user ID.
 *
 * @param {string} userId - The ID of the user whose MFA is to be disabled.
 * @returns {Promise<DisableUserMfaResponse>} A promise that resolves with the disable MFA response.
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await disableUserMfa('user-123');
 * if (response.isSuccess) {
 *   console.log('MFA disabled successfully');
 * }
 */
export const disableUserMfa = async (
  userId: string,
  config: ConfigurationSettings
): Promise<DisableUserMfaResponse> => {
  const payload: DisableUserMfaRequest = {
    userId,
    projectKey: config.blocksKey,
  };
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.mfa.disableUserMfa}`;
  return await clients.post<DisableUserMfaResponse>(url, config.blocksKey, JSON.stringify(payload));
};
